import { useEffect, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  RouteCard,
  RouteBadge,
  UserLocationDot,
  type RouteStatusVM,
  type DestinationButtonStation,
  type LineDisplay,
} from '@metro/route-card'
import type { Route } from '@metro/routing'
import styles from './RouteCardVisualFixture.module.css'

/**
 * Private QA fixture for Playwright phone-overlay snapshots (RouteCard
 * Figma spec 2026-05-22 §7.3). Mounts a single card state inside a fixed
 * iPhone-16-Pro frame (402 × 874), with the route-built dim overlay +
 * A/B `RouteBadge`s + `UserLocationDot` composited above the (blank) map
 * exactly as the host would, so each screenshot is pixel-stable.
 *
 * URL contract:
 *   /__visual/routecard?state=<name>&theme=<light|dark>
 *
 * `state` ∈ collapsed · from · to · from-location · loading · results ·
 * empty · error  (= the 8 Figma screen states).
 *
 * `variant=tablet` mounts the card alone in a 420-wide side-panel column
 * (the desktop / tablet sidebar) instead of the phone frame; the map
 * overlays are phone-only.
 *
 * `theme` defaults to `light`; `dark` forces `<html data-theme="dark">`
 * (deterministic regardless of OS pref). Dev/QA-only route, excluded
 * from indexing via `public/robots.txt` `Disallow: /__visual/`.
 */

type FixtureState =
  | 'collapsed'
  | 'from'
  | 'to'
  | 'from-location'
  | 'loading'
  | 'results'
  | 'empty'
  | 'error'

const VALID_STATES: ReadonlySet<FixtureState> = new Set<FixtureState>([
  'collapsed',
  'from',
  'to',
  'from-location',
  'loading',
  'results',
  'empty',
  'error',
])

const BAKER: DestinationButtonStation = {
  id: 'baker',
  shortLocalName: 'Baker Street',
  lineHexColor: '#B36305',
}
const OXFORD: DestinationButtonStation = {
  id: 'oxford',
  shortLocalName: 'Oxford Circus',
  lineHexColor: '#DC241F',
}

const LINE_LOOKUP: ReadonlyMap<string, LineDisplay> = new Map([
  ['bakerloo', { localName: 'Bakerloo line', hexColor: '#B36305' }],
  ['central', { localName: 'Central line', hexColor: '#DC241F' }],
  ['victoria', { localName: 'Victoria line', hexColor: '#0098D4' }],
])

function ride(lineId: string, durationS: number): Route['legs'][number] {
  return { type: 'ride', line_id: lineId, duration_s: durationS, stops: [] }
}

const RESULTS_ROUTES: readonly Route[] = [
  {
    id: 'r0',
    total_time_s: 720,
    transfer_count: 0,
    walking_time_s: 0,
    legs: [ride('bakerloo', 720)],
  },
  {
    id: 'r1',
    total_time_s: 900,
    transfer_count: 1,
    walking_time_s: 0,
    legs: [ride('central', 480), { type: 'transfer', duration_s: 120 }, ride('victoria', 300)],
  },
  {
    id: 'r2',
    total_time_s: 1080,
    transfer_count: 1,
    walking_time_s: 0,
    legs: [ride('central', 1080)],
  },
]

// Fixed clock so the arrival HH:mm chips render byte-identically.
const FIXED_CLOCK = (): Date => new Date('2026-05-22T12:00:00Z')

function parseState(raw: string | null): FixtureState {
  if (raw !== null && (VALID_STATES as Set<string>).has(raw)) return raw as FixtureState
  return 'collapsed'
}

interface StateModel {
  from: DestinationButtonStation | null
  to: DestinationButtonStation | null
  status: RouteStatusVM
  showLocation: boolean
  badges: ReadonlyArray<{ letter: 'A' | 'B'; color: string; x: number; y: number }>
  dim: boolean
}

function modelFor(state: FixtureState): StateModel {
  // Badge map positions are arbitrary-but-fixed within the 402×874 frame.
  const A = { letter: 'A' as const, color: BAKER.lineHexColor, x: 120, y: 300 }
  const B = { letter: 'B' as const, color: OXFORD.lineHexColor, x: 250, y: 420 }
  switch (state) {
    case 'collapsed':
      return {
        from: null,
        to: null,
        status: { kind: 'collapsed' },
        showLocation: true,
        badges: [],
        dim: false,
      }
    case 'from':
      return {
        from: BAKER,
        to: null,
        status: { kind: 'collapsed' },
        showLocation: false,
        badges: [A],
        dim: false,
      }
    case 'to':
      return {
        from: null,
        to: OXFORD,
        status: { kind: 'collapsed' },
        showLocation: false,
        badges: [B],
        dim: false,
      }
    case 'from-location':
      return {
        from: BAKER,
        to: null,
        status: { kind: 'collapsed' },
        showLocation: true,
        badges: [A],
        dim: false,
      }
    case 'loading':
      return {
        from: BAKER,
        to: OXFORD,
        status: { kind: 'loading' },
        showLocation: false,
        badges: [A, B],
        dim: true,
      }
    case 'results':
      return {
        from: BAKER,
        to: OXFORD,
        status: { kind: 'results', routes: RESULTS_ROUTES, selectedIndex: 0 },
        showLocation: false,
        badges: [A, B],
        dim: true,
      }
    case 'empty':
      return {
        from: BAKER,
        to: OXFORD,
        status: { kind: 'empty' },
        showLocation: false,
        badges: [A, B],
        dim: true,
      }
    case 'error':
      return {
        from: BAKER,
        to: OXFORD,
        status: { kind: 'error' },
        showLocation: false,
        badges: [A, B],
        dim: true,
      }
  }
}

export function RouteCardVisualFixture(): ReactElement {
  const [params] = useSearchParams()
  const state = parseState(params.get('state'))
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light'
  const model = modelFor(state)
  const isTablet = params.get('variant') === 'tablet'

  useEffect(() => {
    const html = document.documentElement
    const prev = html.dataset.theme
    html.dataset.theme = theme
    html.classList.add('visual-fixture-active')
    return () => {
      if (prev === undefined) delete html.dataset.theme
      else html.dataset.theme = prev
      html.classList.remove('visual-fixture-active')
    }
  }, [theme])

  if (isTablet) {
    return (
      <div
        className={styles.sidePanel}
        data-testid="routecard-visual-fixture"
        data-state={state}
        data-theme={theme}
      >
        <RouteCard
          variant="tablet"
          from={model.from}
          to={model.to}
          routeStatus={model.status}
          lineLookup={LINE_LOOKUP}
          onSelectVariant={() => {}}
          onRetry={() => {}}
          onSwap={() => {}}
          clock={FIXED_CLOCK}
        />
      </div>
    )
  }

  return (
    <div
      className={styles.phone}
      data-testid="routecard-visual-fixture"
      data-state={state}
      data-theme={theme}
    >
      {/* Map overlays: A/B badges + user-location dot sit above the map.
          For route-built states they render ABOVE the dim layer too. */}
      {model.showLocation && (
        <div className={styles.dotAnchor} style={{ left: 64, top: 360 }}>
          <UserLocationDot frozen />
        </div>
      )}

      {model.dim && (
        <div className={`${styles.dim} ${theme === 'dark' ? styles.dimDark : styles.dimLight}`} />
      )}

      {model.badges.map((b) => (
        <div key={b.letter} className={styles.badgeAnchor} style={{ left: b.x, top: b.y }}>
          <RouteBadge letter={b.letter} lineHexColor={b.color} />
        </div>
      ))}

      <div className={styles.cardAnchor}>
        <RouteCard
          variant="phone"
          from={model.from}
          to={model.to}
          routeStatus={model.status}
          lineLookup={LINE_LOOKUP}
          onSelectVariant={() => {}}
          onMyLocationTap={model.showLocation ? () => {} : undefined}
          onOpenRouteDetails={() => {}}
          onRetry={() => {}}
          onSwap={() => {}}
          onClose={model.status.kind !== 'collapsed' ? () => {} : undefined}
          clock={FIXED_CLOCK}
        />
      </div>
    </div>
  )
}

export default RouteCardVisualFixture
